import { useEffect, useState, useRef } from 'react'
import '../styles/SideBar.css'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getNumberProfilesForReview, getUnseenConversations, getById } from '../server/server'
import { useSocket } from '../context/socket'
// Logos
import { ReactComponent as Dashboard } from '../assets/svg/dashboard.svg'
import { ReactComponent as Content } from '../assets/svg/content.svg'
import { ReactComponent as Classroom } from '../assets/svg/classroom.svg'
import { ReactComponent as Analyze } from '../assets/svg/analyze.svg'
import { ReactComponent as Chat } from '../assets/svg/chat.svg'
import { ReactComponent as Management } from '../assets/svg/management.svg'
import { ReactComponent as Wellbeing } from '../assets/svg/wellbeing.svg'
import { ReactComponent as Videos } from '../assets/svg/videos.svg'
import { ReactComponent as Settings } from '../assets/svg/settings.svg'
import { ReactComponent as Logout } from '../assets/svg/logout.svg'
import { ReactComponent as CatchUp } from '../assets/svg/catch-up.svg'
import { ReactComponent as Alerts } from '../assets/svg/alerts.svg'
import { ReactComponent as NavPin } from '../assets/svg/nav-pin.svg'
import { ReactComponent as ContentDrop } from '../assets/svg/contentDrop.svg'

import { useUser } from '../auth'
import LogoutModal from './modals/LogoutModal'
import { useInstitution } from '../auth/InstitutionContext'
import i18n from '../i18n'
import { getLanguagePack } from '../utils/util'
import { useAppContext } from '../context/AppContext'
import { sidebarDataPrimary, sidebarDataRegular } from '../constants/Constants'
import { useMemo } from 'react'
import CustomLogoIcon from './common/CustomLogoIcon'
import { isItAccess } from '../constants/Roles'

function SideBar() {
    const [homeStatus, setHomeStatus] = useState('/dashboard')
    const location = useLocation()
    const { user, setUser } = useUser()
    const { appState, setAppState } = useAppContext()
    let history = useHistory()
    const { t, locale } = useTranslation()
    const { socketStates, changeSocketState } = useSocket()
    const [messages, setMessages] = useState([])
    const [profiles, setProfiles] = useState()
    const [collapsed, setCollapsed] = useState(true)
    const [selectedLang, setSelectedLanguage] = useState(i18n.language)
    const [isPinned, setIsPinned] = useState(false)
    const numberOfUnseenConversations = socketStates?.unseenConversations?.length || 0
    const [logoutModalOpen, setLogoutModalOpen] = useState(false)
    const { institution, setInstitution } = useInstitution()
    const primaryUI = !!appState?.primary
    const isDemoSchool = user.institution_id === '5f25b9fc7fc9788dacc0ce7d' || user.institution_id === '603faf90396b69a70480e2b2'
    const isAdmin = user?.role === 'school_admin' || user?.role_coverage?.target_text === 'All students'

    const [showContent, setShowContent] = useState(false)
    const [showAlerts, setShowAlerts] = useState(false)
    const isFirstRender = useRef(true)

    const sidebarData = useMemo(() => {
        return primaryUI ? sidebarDataPrimary() : sidebarDataRegular(numberOfUnseenConversations, isAdmin)
    }, [primaryUI, numberOfUnseenConversations, isAdmin])

    useEffect(() => {
        const pathList = location.pathname.split('/').slice(1)
        const shortenedPath = '/' + pathList.join('/')
        setHomeStatus(shortenedPath)
    }, [location.pathname])

    const loadMessages = async () => {
        const response = await getUnseenConversations()
        if (response.status === 200) {
            changeSocketState({ unseenConversations: response?.data || [] })
        }
    }
    const loadProfiles = async () => {
        const profiles = await getNumberProfilesForReview()
        if (profiles.status === 200) {
            setProfiles(profiles?.data)
        }
    }

    const loadInstitution = async () => {
        const resp = await getById({ collection: 'institutions', id: user.institution_id })
        if (resp.status === 200) {
            setInstitution(resp?.data)
        }
    }

    useEffect(() => {
        if (!isItAccess(user)) {
            loadMessages()
            loadProfiles()
        }
        loadInstitution()
    }, [])

    useEffect(() => {
        const sum = socketStates?.unseenConversations?.length
        const oldName = document.title
        const splitValues = oldName.split(')')
        const removedValues = splitValues[splitValues.length - 1]
        if (!!sum) {
            document.title = `(${sum}) ${removedValues}`
        } else {
            document.title = `${removedValues}`
        }
    }, [socketStates])

    useEffect(() => {
        document.getElementById('home-container').style.marginLeft = isPinned ? '280px' : '80px'
    }, [isPinned])

    const renderIcon = (name, color) => {
        switch (name) {
            case 'dashboard':
                return <Dashboard fill={color} height={17} width={16} />
            case 'content':
                return <Content fill={color} />
            case 'classroom':
                return <Classroom fill={color} />
            case 'analyze':
                return <Analyze fill={color} />
            case 'chat':
                return <Chat fill={color} />
            case 'management':
                return <Management fill={color} />
            case 'wellbeing':
                return <Wellbeing fill={color} />
            case 'videos':
                return <Videos fill={color} />
            case 'settings':
                return <Settings fill={color} />
            case 'logout':
                return <Logout fill={color} />
            case 'catch-up':
                return <CatchUp fill={color} />
            case 'alerts':
                return <Alerts stroke={color} />
            default:
                return <Dashboard fill={color} />
        }
    }

    const langs = getLanguagePack(user?.institution_id)

    const handleLangChange = (code) => {
        setSelectedLanguage(code)
        i18n.changeLanguage(code)
        localStorage.setItem('locale', code)
    }
    const closeModal = () => {
        setLogoutModalOpen(false)
    }

    const openLogoutModal = () => {
        setLogoutModalOpen(true)
    }

    const logout = () => {
        localStorage.clear()
        setUser({})
        setAppState({})
        history.push('/')
        setLogoutModalOpen(false)
    }
    useEffect(() => {
        setSelectedLanguage(i18n.language)
    }, [i18n.language])

    useEffect(() => {
        if (isPinned) document.getElementById('root').classList.add('sidebarPinned')
        else document.getElementById('root').classList.remove('sidebarPinned')
    }, [isPinned])

    const handleState = () => {
        const nextState = !appState?.primary
        setAppState({ ...appState, primary: nextState })
        localStorage.setItem('uiMode', nextState ? 'primary' : 'regular')

        history.push(`/dashboard`)
    }
    const handleShow = (item) => {
        setShowContent(false)
        setShowAlerts(false)
        if (item.title == 'content') setShowContent(!showContent)
        if (item.title == 'Alerts') setShowAlerts(!showAlerts)
    }

    const setIsOpen = (item) => {
        if (item.title == 'content' && showContent) return true
        else if (item.title == 'Alerts' && showAlerts) return true
        else {
            return false
        }
    }

    return (
        <div
            id="sideBar"
            className={`side-bar bg-deepOcean ${collapsed ? 'side-bar-collapsed' : 'side-bar-open'}`}
            style={collapsed ? { width: 80 } : {}}
            onMouseLeave={() => {
                setShowContent(false)
                setShowAlerts(false)
            }}
        >
            <div
                className="flex flex-col items-center h-[100vh] w-full"
                onMouseLeave={() => {
                    setCollapsed(isPinned ? false : true)
                }}
                onMouseEnter={() => setCollapsed(false)}
            >
                <div className="nav-pin bg-deepOcean" onClick={() => setIsPinned(!isPinned)}>
                    <NavPin fill={isPinned ? 'var(--light-mint)' : 'var(--text-light)'} style={{ display: 'block' }} />
                </div>
                <div
                    className="flex flex-row items-center justify-center mt-6 min-h-[100px] relative cursor-pointer"
                    onClick={() => {
                        history.push(`/dashboard`)
                    }}
                >
                    <div className={`upstrive-logo-collapsed absolute `}>
                        <CustomLogoIcon width={collapsed ? 80 : 200} height={200} style={{ transition: 'all 300ms ease-in-out' }} />
                    </div>
                </div>
                <div className="my-[5px] min-h-6 min-w-6 ">
                    <span
                        className={`px-[30px] text-textLight text-[16px] font-bold w-full ellipsis max-w-[270px] text-ellipsis ${collapsed ? 'opacity-0' : 'opacity-100'}`}
                        style={{ display: collapsed ? 'none' : 'block' }}
                    >
                        {institution.name}
                    </span>
                </div>
                <div className=" mt-1 xl3:mt-6  p-2 pointer font-bold text-lg border-grey text-grey rounded-full flex flex-row w-fit" />
                <div className="mt-1 xl3:mt-6 w-full flex flex-col pl-[30px] overflow-y-auto overflow-x-hidden">
                    <div className=" text-textLight font-bold text-lg flex flex-col justify-between h-[100vh]">
                        <div className="link-list">
                            {sidebarData?.map((item, index) => {
                                if (item.name === 'videos' && i18n.language !== 'en') return null
                                if (user.hasOnlyPrimaryStudents && ['content', 'chat'].includes(item.name)) return null
                                if (isItAccess(user) && item.name !== 'management') return null
                                const selected = item.path?.some((p) => p.startsWith(homeStatus))
                                const isOpen = setIsOpen(item)

                                return (
                                    <div key={item?.path[0]} data-title={t(item.title)} className={`my-0 xl3:my-3 ${item.className}`}>
                                        {item?.options && item?.options?.length > 0 ? (
                                            <div>
                                                <div onClick={() => handleShow(item)} className="pointer  cursor-pointer">
                                                    <div className={`h-[45px] 2xl:h-[52px] flex flex-row  items-center relative transition-[height]`}>
                                                        <div className="items-center">{renderIcon(item?.name, selected ? 'var(--light-mint)' : 'var(--text-light)')}</div>
                                                        {item?.icon}
                                                        {!collapsed && (
                                                            <span className={`px-3 flex justify-between items-center w-full ellipsis ${selected ? ' text-lightMint' : ''}`}>
                                                                {t(item.title)} <ContentDrop />
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={`items-start pl-10 ${isOpen ? 'flex flex-col' : 'hidden'}`}>
                                                    {item?.options?.map((option, index) => {
                                                        return (
                                                            <Link
                                                                to={`${option.path}`}
                                                                className="my-2"
                                                                onClick={() => {
                                                                    setHomeStatus(option.path)
                                                                }}
                                                                key={index}
                                                            >
                                                                <div className={`leading-[25px] ${option?.path === homeStatus ? 'text-lightMint' : ''}`}>{t(option?.title)}</div>
                                                            </Link>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        ) : (
                                            <Link to={item?.path[0]}>
                                                <div className={`h-[45px] 2xl:h-[52px] flex flex-row  items-center relative w-full cursor-pointer transition-[height]`}>
                                                    <div className="items-center">{renderIcon(item?.name, selected ? 'var(--light-mint)' : 'var(--text-light)')}</div>
                                                    {!!item?.icon && <div className="chat-badge-wrapper">{item?.icon}</div>}
                                                    {!collapsed && (
                                                        <span className={`px-3 w-full ellipsis text-ellipsis ${selected ? ' text-lightMint' : ''}`}>{t(item.title)}</span>
                                                    )}
                                                </div>
                                            </Link>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                        <div className="bottom-container">
                            {!!user?.hasPrimaryClasses && !user.hasOnlyPrimaryStudents && (
                                <div className=" nav-text cursor-pointer">
                                    <div className={`h-[45px] 2xl:h-[52px] flex flex-row  items-center relative transition-[height]`} onClick={() => handleState()}>
                                        <div className="items-center">{renderIcon('switch', 'var(--text-light)')}</div>
                                        {!collapsed && (
                                            <span className="px-3 w-full ellipsis text-ellipsis">{appState.primary ? t('Open AlkalmaUp') : t('Open Alkalma Early')}</span>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div
                                className=" cursor-pointer nav-text"
                                onClick={() => {
                                    setHomeStatus('/settings')
                                }}
                            >
                                <Link to="/settings">
                                    <div className={`h-[52px] flex flex-row  items-center relative w-full cursor-pointer`}>
                                        <div className="items-center">{renderIcon('settings', homeStatus === '/settings' ? 'var(--light-mint)' : 'var(--text-light)')}</div>
                                        {!collapsed && <span className={`px-3 ${homeStatus === '/settings' ? ' text-textLight' : ''}`}>{t('settings')}</span>}
                                    </div>
                                </Link>
                            </div>
                            <div className=" nav-text cursor-pointer">
                                <div className={`h-[52px] flex flex-row  items-center relative`} onClick={() => openLogoutModal()}>
                                    <div className="items-center">{renderIcon('logout', 'var(--text-light)')}</div>
                                    {!collapsed && <span className="px-3 w-full ellipsis text-ellipsis">{t('logout')}</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <LogoutModal modalIsOpen={logoutModalOpen} closeModal={closeModal} logout={logout} />
        </div>
    )
}

export default SideBar

const SingleItem = ({ item, homeStatus, renderIcon, collapsed, setHomeStatus }) => {
    const { t, locale } = useTranslation()
    const selected = useMemo(() => item?.path?.includes(homeStatus), [item?.path, homeStatus])
    const [showContent, setShowContent] = useState(false)
    return (
        <div key={item?.path[0]} data-title={t(item.title)} className={`my-0 xl3:my-3 ${item.className}`}>
            {item?.options && item?.options?.length > 0 ? (
                <div>
                    <div onClick={() => setShowContent(!showContent)} className="pointer  cursor-pointer">
                        <div className={`h-[52px] flex flex-row  items-center relative`}>
                            <div className="items-center">{renderIcon(item?.name, selected ? 'var(--light-mint)' : 'var(--text-light)')}</div>
                            {item?.icon}
                            {!collapsed && (
                                <span className={`px-3 flex justify-between items-center w-full ellipsis ${selected ? ' text-highlightColor' : ''}`}>
                                    {t(item.title)} <ContentDrop />
                                </span>
                            )}
                        </div>
                    </div>
                    <div className={`items-start pl-10 ${showContent ? 'flex flex-col' : 'hidden'}`}>
                        {item?.options?.map((option, index) => {
                            return (
                                <Link
                                    to={`${option.path}`}
                                    className="my-2"
                                    onClick={() => {
                                        setHomeStatus(option.path)
                                    }}
                                    key={index}
                                >
                                    <div className={`leading-[25px] ${option?.path === homeStatus ? 'text-lightMint' : ''}`}>{t(option?.title)}</div>
                                </Link>
                            )
                        })}
                    </div>
                </div>
            ) : (
                <Link to={item?.path[0]}>
                    <div className={`h-[52px] flex flex-row  items-center relative w-full cursor-pointer`}>
                        <div className="items-center">{renderIcon(item?.name, selected ? 'var(--light-mint)' : 'var(--text-light)')}</div>
                        {!!item?.icon && <div className="chat-badge-wrapper">{item?.icon}</div>}
                        {!collapsed && <span className={`px-3 w-full ellipsis text-ellipsis ${selected ? ' text-lightMint' : ''}`}>{t(item.title)}</span>}
                    </div>
                </Link>
            )}
        </div>
    )
}
